/**
 * The exam timetable a pupil and their parents see: every paper in the order
 * it comes, each with a countdown bar.
 *
 * ORDER IS THE POINT (the school's ask): the first paper first, then what
 * follows. Papers already written drop into a folded list at the end rather
 * than sitting above the next one.
 *
 * The bar runs from the first day of term to the start of the session, and
 * warms as the day comes: amber inside a week, red inside two days. Times are
 * the school's session times — a pupil is in the room for the whole session,
 * whatever the paper's length — and the page says so once.
 */
import { useState } from 'react';
import { AlarmClock, CalendarClock, CheckCircle2, Download, Loader2 } from 'lucide-react';
import {
  countdownFor,
  daysLabel,
  groupByDay,
  nextSitting,
  SESSION_NAMES,
  type Countdown,
  type ExamSession,
} from '@/lib/examTimetable';
import type { SeriesSitting } from './useExamTimetable';

const SESSION_NAME = SESSION_NAMES;

function dayName(iso: string, long = false): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    weekday: long ? 'long' : 'short', day: 'numeric', month: long ? 'long' : 'short', timeZone: 'UTC',
  });
}

/** Each session's times, where every paper in it shares them — Cambridge's
 *  whole-session sittings. LKC's own exams give each paper its own end, and
 *  then a session has no single time worth printing. */
function uniformSessions(sittings: SeriesSitting[]): Partial<Record<ExamSession, { start: string; end: string }>> {
  const out: Partial<Record<ExamSession, { start: string; end: string }>> = {};
  for (const k of ['AM', 'MM', 'PM'] as const) {
    const these = sittings.filter((s) => s.session === k && !s.window);
    if (these.length && these.every((s) => s.start === these[0].start && s.end === these[0].end)) {
      out[k] = { start: these[0].start, end: these[0].end };
    }
  }
  return out;
}

/** "Morning session 07:30–11:30, afternoon session 12:30–15:30." — read off
 *  the papers, so a change to the series' times cannot leave this sentence behind. */
function sessionTimes(sittings: SeriesSitting[]): string {
  const u = uniformSessions(sittings);
  const parts = (['AM', 'MM', 'PM'] as const)
    .filter((k) => u[k])
    .map((k) => `${SESSION_NAME[k].toLowerCase()} session ${u[k]!.start}–${u[k]!.end}`)
    .join(', ');
  return parts ? `${parts.charAt(0).toUpperCase()}${parts.slice(1)}.` : '';
}

function barTone(c: Countdown): string {
  if (c.state === 'done') return 'bg-[var(--sp-line-2)]';
  if (c.state === 'inSession' || c.urgency === 'imminent') return 'bg-rose-500';
  if (c.urgency === 'soon') return 'bg-amber-500';
  return 'bg-primary dark:bg-sky-400';
}

function labelTone(c: Countdown): string {
  if (c.state === 'inSession' || c.urgency === 'imminent') return 'text-rose-600 dark:text-rose-300';
  if (c.urgency === 'soon') return 'text-amber-700 dark:text-amber-300';
  return 'text-[var(--sp-muted)]';
}

function Bar({ c }: { c: Countdown }) {
  return (
    <div
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-[var(--sp-track)]"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(c.fraction * 100)}
      aria-label={daysLabel(c)}
    >
      <div className={`absolute inset-y-0 left-0 rounded-full ${barTone(c)}`} style={{ width: `${Math.max(2, c.fraction * 100)}%` }} />
    </div>
  );
}

function PaperRow({ s, now }: { s: SeriesSitting; now: number }) {
  const c = countdownFor(s, now, s.countdownFrom);
  const done = c.state === 'done';
  return (
    <li className={`px-4 py-3 ${done ? 'opacity-70' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink dark:text-slate-100">
            {s.subject}
            {s.part && <span className="font-normal text-[var(--sp-body)]"> · {s.part}</span>}
          </p>
          <p className="mt-0.5 text-xs text-[var(--sp-muted)]">
            <span className="tabular-nums">{s.code}</span>
            {s.window
              ? <>{' · '}practical: come at {s.start}, the school will explain the order</>
              : <>{' · '}{s.sessionLabel ?? `${SESSION_NAME[s.session]} session`}, {s.start}–{s.end}</>}
            {s.duration && <>{' · '}paper {s.duration}</>}
          </p>
          {s.note && <p className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-200">{s.note}</p>}
        </div>
        <p className={`shrink-0 text-right text-xs font-semibold ${labelTone(c)}`}>
          {done ? <CheckCircle2 className="inline h-4 w-4" aria-label="Done" /> : daysLabel(c)}
        </p>
      </div>
      {!done && <div className="mt-2"><Bar c={c} /></div>}
    </li>
  );
}

export default function ExamTimetableView({
  sittings, loading, error, now, who, title, print,
}: {
  sittings: SeriesSitting[];
  loading: boolean;
  error: string | null;
  now: number;
  /** "you" for a pupil; the child's first name for a parent. */
  who?: string;
  title?: string;
  /** Who the printed copy is FOR. Omitted where there is nobody to name —
   *  the download then does not appear rather than printing "undefined". */
  print?: { pupilName: string; className?: string; candidateNumber?: string; seriesTitle?: string };
}) {
  const [printing, setPrinting] = useState(false);

  if (loading) {
    return <p className="px-1 py-6 text-sm text-[var(--sp-muted)]">Loading the exam timetable…</p>;
  }
  if (error) {
    return <p className="border border-[var(--sp-line)] bg-[var(--sp-card)] px-5 py-4 text-sm text-rose-600 dark:text-rose-300">{error}</p>;
  }
  if (!sittings.length) {
    return (
      <div className="border border-[var(--sp-line)] bg-[var(--sp-card)] px-5 py-6 text-sm text-[var(--sp-body)]">
        No exams are timetabled {who && who !== 'you' ? `for ${who} ` : ''}yet. When the school publishes an exam timetable, the papers will appear here.
      </div>
    );
  }

  const upcoming = sittings.filter((s) => countdownFor(s, now, s.countdownFrom).state !== 'done');
  const done = sittings.filter((s) => countdownFor(s, now, s.countdownFrom).state === 'done');
  const next = nextSitting(sittings, now);
  const nextC = next ? countdownFor(next, now, next.countdownFrom) : null;
  const first = sittings[0];
  const last = sittings[sittings.length - 1];

  /* Asked for by the students (2026-09-19): they want it on a wall. The PDF
     module pulls in jsPDF and the embedded font — about a megabyte — so it
     is imported on the click and never on a pupil's first load. */
  const download = async () => {
    if (!print) return;
    setPrinting(true);
    try {
      const { buildExamTimetablePdf } = await import('@/lib/examTimetablePdf');
      const pdf = await buildExamTimetablePdf({
        pupilName: print.pupilName,
        className: print.className,
        candidateNumber: print.candidateNumber,
        seriesTitle: print.seriesTitle ?? 'Exam timetable',
        sessions: uniformSessions(sittings),
        papers: sittings.map((s) => ({
          code: s.code,
          subject: s.subject,
          part: s.part,
          date: s.date,
          session: s.session,
          start: s.start,
          end: s.end,
          minutes: s.minutes,
          window: s.window,
        })),
      });
      pdf.save(`exam-timetable-${print.pupilName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--sp-muted)]">
            {title ?? 'Exam timetable'}
          </p>
          <p className="mt-1 text-sm text-[var(--sp-body)]">
            {sittings.length} papers, {dayName(first.date)} to {dayName(last.date)}
            {done.length ? ` · ${done.length} already sat, not shown` : ''}
          </p>
        </div>
        {print && (
          <button
            type="button" onClick={() => { void download(); }} disabled={printing}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryLight disabled:opacity-50 dark:text-white"
          >
            {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Print my timetable
          </button>
        )}
      </div>

      {next && nextC && (
        <div className="border border-[var(--sp-line)] bg-[var(--sp-card)] px-5 py-4">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--sp-muted)]">
            <AlarmClock className="h-3.5 w-3.5" /> Next paper
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="sp-display text-lg text-ink dark:text-slate-100">
                {next.subject}{next.part ? ` · ${next.part}` : ''}
              </p>
              <p className="text-sm text-[var(--sp-body)]">
                {dayName(next.date, true)}
                {next.window ? ', time to be given by the school' : `, ${(next.sessionLabel ?? `${SESSION_NAME[next.session]} session`).toLowerCase()} ${next.start}–${next.end}`}
              </p>
            </div>
            <p className={`text-2xl font-bold tabular-nums ${labelTone(nextC)}`}>{daysLabel(nextC)}</p>
          </div>
          <div className="mt-3"><Bar c={nextC} /></div>
        </div>
      )}

      <p className="flex items-start gap-2 px-1 text-xs text-[var(--sp-muted)]">
        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {sessionTimes(sittings)
          ? <>{sessionTimes(sittings)} Be in the exam room for the whole session, however long the paper is.</>
          : 'Be at the exam room before your paper starts.'}
      </p>

      {groupByDay(upcoming).map((day) => {
        const c = countdownFor(day.sittings[0], now, day.sittings[0].countdownFrom);
        return (
          <section key={day.date} className="border border-[var(--sp-line)] bg-[var(--sp-card)]">
            <header className="flex items-center justify-between gap-3 border-b border-[var(--sp-line)] px-4 py-2">
              <h3 className="text-sm font-semibold text-ink dark:text-slate-100">{dayName(day.date, true)}</h3>
              <span className={`text-xs font-semibold ${labelTone(c)}`}>{daysLabel({ ...c, state: c.state === 'inSession' ? 'today' : c.state })}</span>
            </header>
            <ul className="divide-y divide-[var(--sp-line)]">
              {day.sittings.map((s) => <PaperRow key={s.code} s={s} now={now} />)}
            </ul>
          </section>
        );
      })}

      {/* Papers already sat are left off entirely (the school, 2026-10-05): this
          is a live view of what is still to come. The count stays in the line
          at the top, and the printed timetable still lists every paper. */}
      {!upcoming.length && (
        <div className="border border-[var(--sp-line)] bg-[var(--sp-card)] px-5 py-6 text-sm text-[var(--sp-body)]">
          Every paper in this series has been sat. Well done.
        </div>
      )}
    </div>
  );
}
